import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useAsync } from '@react-hookz/web';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AISummary } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/AISummary/index.js';
import { FilterDropdown } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/FilterDropdown.js';
import { RatingGroups } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/RatingGroups/index.js';
import { ReviewItem } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/ReviewItem.js';
import { ReviewsHeader } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/ReviewsHeader.js';
import { SortingDropdown } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/SortingDropdown.js';
import { Summary } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/components/Summary/index.js';
import { useFilterOptions } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/hooks/useFilterOptions.js';
import { useSortingOptions } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/hooks/useSortingOptions.js';
import { fetchHighlightedReviews } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/utils/fetchHighlightedReviews.js';
import { fetchRating } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/utils/fetchRating.js';
import { mapFilterOptionsToFilters } from '../../../../components/HotelDetailsOverlay/components/AmadeusRatingSection/utils/mapFilterOptionsToFilters.js';
import { useConfig } from '../../../../components/HotelDetailsOverlay/context/Config.js';
import { Pagination, PaginationItem, Flex } from '../../../../primitives/index.js';
export const AmadeusRatingSection = ({
  data: {
    hotelId,
    url
  },
  onSelectReview
}) => {
  const {
    t
  } = useTranslation('hotelDetailsOverlay');
  const {
    amadeusRating: {
      recommendationRateEnabled,
      reviewSource,
      splitTreatment,
      language,
      onClickReviewImage
    }
  } = useConfig();
  const ownSource = reviewSource === 'OwnAma' || reviewSource === 'OwnCEE';
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState([ownSource ? 'withText' : 'all']);
  const [sorting, setSorting] = useState('latest');
  const [loading, setLoading] = useState(true);
  const headerWrapper = useRef(null);
  const filterOptions = useFilterOptions(ownSource);
  const sortingOptions = useSortingOptions();
  const handleFilterChange = nextValue => {
    setFilters(nextValue);
    setPage(1);
  };
  const handleSortingChange = nextValue => {
    setSorting(nextValue);
    setPage(1);
  };
  const getRating = useCallback(async () => {
    const {
      traveledAs,
      hasPhotos,
      hasTextRating,
      recommendation
    } = mapFilterOptionsToFilters(filters);
    setLoading(true);
    const rating = await fetchRating({
      url,
      hotelId,
      page,
      sort: sorting,
      traveledAs,
      splitTreatment,
      lang: language,
      src: reviewSource,
      hasPhotos,
      hasTextRating,
      recommendation
    });
    setData(rating);
    setLoading(false);
  }, [url, hotelId, page, filters, sorting, splitTreatment, language, reviewSource]);
  const [highlightedReviewsQuery, highlightedReviewsQueryActions] = useAsync(fetchHighlightedReviews);
  useEffect(() => {
    getRating();
  }, [getRating]);
  useEffect(() => {
    if (reviewSource === 'OwnCEE' && language) {
      highlightedReviewsQueryActions.execute({
        baseUrl: url,
        hotelId,
        language
      });
    }
  }, [reviewSource, highlightedReviewsQueryActions, url, hotelId, language]);
  if (!loading && !data) {
    return _jsx("div", {
      children: t('amadeusRatingSection.noRatings')
    });
  }
  const pageCount = Math.ceil((data?.count || 0) / 10);
  const highlightedReviews = Array.isArray(highlightedReviewsQuery.result?.reviews) ? highlightedReviewsQuery.result.reviews.slice(0, 1) : [];
  return _jsxs(Flex, {
    spacing: "pages/small",
    children: [_jsx(Summary, {
      loading: loading,
      rating: data?.rating || 0,
      recommendation: recommendationRateEnabled && data?.recommendation != null ? data.recommendation : null,
      reviewsCount: data?.count || 0
    }), _jsxs(Flex, {
      spacing: "dividers/medium",
      divider: true,
      children: [_jsx(RatingGroups, {
        loading: loading,
        groups: data?.ratingGroups
      }), data?.summary && _jsx(AISummary, {
        summaryText: data.summary
      }), highlightedReviews.length > 0 && _jsx(Flex, {
        spacing: "pages/small",
        children: highlightedReviews.map((review, index) => _jsx(ReviewItem, {
          review: review,
          highlighted: true,
          onClickDetailButton: () => {
            onSelectReview(review);
          },
          onClickImage: imageIndex => {
            onClickReviewImage?.({
              review,
              imageIndex
            });
          }
        }, index))
      }), _jsxs(Flex, {
        ref: headerWrapper,
        spacing: "pages/small",
        children: [_jsxs(ReviewsHeader, {
          reviewsCount: data?.count || 0,
          children: [_jsx(FilterDropdown, {
            value: filters,
            options: filterOptions,
            isMulti: ownSource,
            onChange: handleFilterChange
          }), _jsx(SortingDropdown, {
            value: sorting,
            options: sortingOptions,
            onChange: handleSortingChange
          })]
        }), _jsx(Flex, {
          spacing: "dividers/medium",
          divider: true,
          children: data?.reviews?.map((review, index) => _jsx(ReviewItem, {
            review: review,
            onClickDetailButton: () => {
              onSelectReview(review);
            },
            onClickImage: imageIndex => {
              onClickReviewImage?.({
                review,
                imageIndex
              });
            }
          }, index))
        })]
      }), pageCount > 1 && _jsx(Pagination, {
        page: page,
        pageCount: pageCount,
        size: "small",
        label: {
          previous: t('amadeusRatingSection.previous'),
          next: t('amadeusRatingSection.next')
        },
        renderItem: props => _createElement(PaginationItem, {
          ...props,
          key: `${props.type}_${props.page}_${props.label}_${props.selected}`,
          // @ts-expect-error ...
          style: {
            background: props.disabled ? 'transparent' : undefined
          }
        }),
        onChange: (_event, page) => {
          headerWrapper.current?.scrollIntoView({
            behavior: 'smooth'
          });
          setPage(page);
        }
      })]
    })]
  });
};